(function () {
  if (customElements.get('product-comparison')) return;

  const read = (host, selector, fallback) => {
    const el = host.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return fallback;
    }
  };

  class ProductComparison extends HTMLElement {
    connectedCallback() {
      this.mount = this.querySelector('[data-pcmp-mount]');
      if (!this.mount) return;

      this.anchor = read(this, '[data-pcmp-anchor]', null);
      this.candidates = read(this, '[data-pcmp-candidates]', []);
      this.t = read(this, '[data-pcmp-strings]', {});

      if (!this.anchor || !this.candidates.length) return;

      // Mobile shows two products side by side, the anchor plus one pick;
      // three columns of specs do not fit a phone
      this.isMobile = window.matchMedia('(max-width: 767px)').matches;
      this.max = this.isMobile ? 1 : (parseInt(this.dataset.compareMax, 10) || 3);
      const hintEl = this.querySelector('.pcmp__hint');
      this._hintDefault = hintEl ? hintEl.textContent : '';
      if (this.isMobile) {
        if (hintEl) hintEl.textContent = 'Pick one to compare side by side';
      }

      this.chosen = [this.nearestByPrice().id];

      this.previewRows = 3;
      this.expanded = false;

      this.differencesOnly = true;

      this.rows = Object.keys(this.anchor.specs);
      this.candidates.forEach((c) => {
        Object.keys(c.specs).forEach((k) => {
          if (this.rows.indexOf(k) === -1) this.rows.push(k);
        });
      });

      // A field nobody in the set fills is noise, not information: shed
      // fields like Cladding Thickness showed Not specified down every
      // column on climbing frames
      this.rows = this.rows.filter((f) => {
        if ((this.anchor.specs[f] || '').trim()) return true;
        return this.candidates.some((c) => (c.specs[f] || '').trim());
      });

      this.render();
      this.bind();

      this.classList.add('is-pcmp-ready');
    }

    togglePick(id, input) {
      // The cap re-checks the viewport at interaction time, so a window
      // resized since page load gets the right limit rather than the one
      // latched at connect
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      this.max = mobile ? 1 : (parseInt(this.dataset.compareMax, 10) || 3);
      const at = this.chosen.indexOf(id);
      if (at === -1) {
        if (this.chosen.length >= this.max) {
          if (input) input.checked = false;
          this.flashLimit(mobile);
          return;
        }
        this.chosen.push(id);
      } else {
        this.chosen.splice(at, 1);
      }
      this.render();
    }

    flashLimit(mobile) {
      const hint = this.querySelector('.pcmp__hint');
      if (!hint) return;
      hint.textContent = mobile ? 'Untick the current pick first' : 'Three is the max, untick one first';
      hint.classList.add('is-limit');
      clearTimeout(this._limitT);
      this._limitT = setTimeout(() => {
        hint.classList.remove('is-limit');
        hint.textContent = mobile ? 'Pick one to compare side by side' : this._hintDefault;
      }, 2200);
    }

    nearestByPrice() {
      const anchor = this.anchor.price || 0;
      return this.candidates
        .slice()
        .sort((a, b) => {
          const da = Math.abs((a.price || 0) - anchor);
          const db = Math.abs((b.price || 0) - anchor);
          if (da !== db) return da - db;
          return (a.price || 0) - (b.price || 0);
        })[0];
    }

    money(pence) {
      if (!pence) return '';
      return '£' + (pence / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    columns() {
      return [this.anchor].concat(this.chosen.map((id) => this.candidates.filter((c) => c.id === id)[0]).filter(Boolean));
    }

    differs(field) {
      const values = this.columns().map((p) => (p.specs[field] || '').trim().toLowerCase());
      return values.some((v) => v !== values[0]);
    }

    render() {
      const picker = this.candidates
        .map((c) => {
          const on = this.chosen.indexOf(c.id) !== -1;
          const full = !on && this.chosen.length >= this.max;
          return (
            '<label class="pcmp__pick' + (on ? ' is-on' : '') + (full ? ' is-full' : '') + '">' +
            '<input type="checkbox" data-pcmp-toggle="' + c.id + '"' + (on ? ' checked' : '') + (full ? ' aria-disabled="true"' : '') + '>' +
            '<span class="pcmp__pick-media">' + (c.image ? '<img src="' + this.esc(c.image) + '" alt="" loading="lazy">' : '') + '</span>' +
            '<span class="pcmp__pick-title">' + this.esc(c.title) + '</span>' +
            '<span class="pcmp__pick-price">' + this.money(c.price) + '</span>' +
            '</label>'
          );
        })
        .join('');

      const cols = this.columns();
      const all = this.rows.filter((f) => !this.differencesOnly || this.differs(f));
      const rows = this.expanded ? all : all.slice(0, this.previewRows);
      const more = all.length - rows.length;

      const allSame = this.chosen.length > 0 && this.differencesOnly && rows.length === 0;

      const head =
        '<tr role="row"><th scope="col" role="columnheader" class="pcmp__corner"></th>' +
        cols
          .map((p, i) => {
            const isAnchor = i === 0;
            return (
              '<th scope="col" role="columnheader" class="pcmp__col' + (isAnchor ? ' is-anchor' : '') + '">' +
              (isAnchor ? '<span class="pcmp__flag">' + this.esc(this.t.thisProduct || '') + '</span>' : '') +
              (p.image ? '<img class="pcmp__col-img" src="' + this.esc(p.image) + '" alt="" loading="lazy">' : '') +
              '<a class="pcmp__col-title" href="' + this.esc(p.url) + '">' + this.esc(p.title) + '</a>' +
              '<span class="pcmp__col-price">' + this.money(p.price) + '</span>' +
              (isAnchor
                ? ''
                : '<button class="pcmp__remove" type="button" data-pcmp-remove="' + p.id + '" aria-label="' + this.esc(String(this.t.removeProduct || 'Remove {{ name }}').replace('{{ name }}', p.title)) + '">&times;</button>') +
              '</th>'
            );
          })
          .join('') +
        '</tr>';

      const body = rows
        .map((f) => {
          const diff = cols.length > 1 && this.differs(f);
          return (
            '<tr role="row" class="pcmp__row' + (diff ? ' is-diff' : '') + '">' +
            '<th scope="row" role="rowheader">' + this.esc(f) + '</th>' +
            cols
              .map((p) => {
                let v = (p.specs[f] || '').trim();
                if (v === 'true') v = 'Yes';
                if (v === 'false') v = 'No';
                return (
                  '<td role="cell">' +
                  (v ? this.esc(v) : '<span class="pcmp__blank">' + this.esc(this.t.notSpecified || '') + '</span>') +
                  '</td>'
                );
              })
              .join('') +
            '</tr>'
          );
        })
        .join('');

      const atLimit = this.chosen.length >= this.max;

      this.mount.innerHTML =
        (this.t.pickHint ? '<p class="pcmp__hint">' + this.esc(this.t.pickHint) + '</p>' : '') +
        '<div class="pcmp__picker" role="group">' + picker + '</div>' +
        (atLimit ? '<p class="pcmp__limit">' + this.esc(this.t.limitReached || '') + '</p>' : '') +
        (this.chosen.length
          ? '<div class="pcmp__controls">' +
            '<label class="pcmp__diff"><input type="checkbox" data-pcmp-diff' + (this.differencesOnly ? ' checked' : '') + '> ' +
            this.esc(this.t.differencesOnly || '') + '</label>' +
            '</div>' +
            (allSame ? '<p class="pcmp__same">' + this.esc(this.t.identical || '') + '</p>' : '') +
            '<div class="pcmp__scroll' + (cols.length > 3 ? ' is-pcmp-wide' : '') + '"><table role="table" class="pcmp__table' + (cols.length > 3 ? ' is-pcmp-wide' : '') + '" style="--pcmp-cols: ' + cols.length + '">' +
            '<thead role="rowgroup">' + head + '</thead>' +
            '<tbody role="rowgroup">' + body + '</tbody></table></div>' +
            (more > 0
              ? '<div class="pcmp__more">' +
                '<button class="button button--lg" type="button" is="hover-button" data-pcmp-expand>' +
                '<span class="btn-fill" data-fill></span>' +
                '<span class="btn-text">' +
                this.esc(String(this.t.showAll || '').replace('{{ count }}', more)) +
                '</span>' +
                '</button></div>'
              : '')
          : '');

      this.bindEdges();

      this.syncStatus(atLimit ? this.t.limitReached : allSame ? this.t.identical : '');
    }

    syncStatus(message) {
      const el = this.querySelector('[data-pcmp-status]');
      if (el) el.textContent = message || '';
    }

    syncEdges() {
      const picker = this.mount && this.mount.querySelector('.pcmp__picker');
      if (!picker) return;

      const max = picker.scrollWidth - picker.clientWidth;
      const x = picker.scrollLeft;

      picker.classList.toggle('has-more-right', max > 1 && x < max - 1);
      picker.classList.toggle('has-more-left', max > 1 && x > 1);
    }

    bindEdges() {
      const picker = this.mount && this.mount.querySelector('.pcmp__picker');
      if (!picker) return;

      picker.addEventListener('scroll', () => this.syncEdges(), { passive: true });

      this.syncEdges();
      requestAnimationFrame(() => this.syncEdges());

      if (!this.edgeLoadBound) {
        this.edgeLoadBound = true;
        window.addEventListener('load', () => this.syncEdges(), { once: true });
      }
    }

    bind() {
      window.addEventListener('resize', () => this.syncEdges());

      this.addEventListener('change', (e) => {
        const toggle = e.target.closest('[data-pcmp-toggle]');
        if (toggle) {
          this.togglePick(parseInt(toggle.dataset.pcmpToggle, 10), toggle);
          return;
        }

        if (e.target.closest('[data-pcmp-diff]')) {
          this.differencesOnly = e.target.checked;
          this.render();
        }
      });

      this.addEventListener('click', (e) => {
        // One deterministic path for the whole card: drive the state
        // directly instead of forwarding synthetic clicks into the
        // checkbox, which interacted badly with native label toggling in
        // some browsers and read as clicks doing nothing
        const pick = e.target.closest('.pcmp__pick');
        if (pick) {
          e.preventDefault();
          const input = pick.querySelector('input[data-pcmp-toggle]');
          if (input) this.togglePick(parseInt(input.dataset.pcmpToggle, 10), input);
          return;
        }

        if (e.target.closest('[data-pcmp-expand]')) {
          this.expanded = true;
          this.render();
          return;
        }

        const remove = e.target.closest('[data-pcmp-remove]');
        if (!remove) return;

        const id = parseInt(remove.dataset.pcmpRemove, 10);
        const at = this.chosen.indexOf(id);
        if (at !== -1) {
          this.chosen.splice(at, 1);
          this.render();
        }
      });
    }
  }

  customElements.define('product-comparison', ProductComparison);
})();
